import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, MapPin, Home, Euro, Ruler, Trash2, Building2, MoreVertical, Edit, Eye, Search, Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { validatePostalCode } from '@/lib/validators';
import { PropertyDocuments } from '@/components/properties/PropertyDocuments';
import { PropertyMortgages } from '@/components/properties/PropertyMortgages';
import { ImportPropertiesDialog } from '@/components/properties/ImportPropertiesDialog';

import { Separator } from '@/components/ui/separator';

const propertyTypes = [
  { value: 'immeuble', label: 'Immeuble' },
  { value: 'zone_industrielle', label: 'Zone industrielle' },
  { value: 'appartement', label: 'Appartement' },
  { value: 'maison', label: 'Maison' },
  { value: 'bureaux', label: 'Bureaux' },
  { value: 'entrepot', label: 'Entrepôt' },
  { value: 'local_commercial', label: 'Local commercial' },
  { value: 'parking', label: 'Parking' },
  { value: 'lot_mixte', label: 'Lot mixte' },
  { value: 'autre', label: 'Autre' },
];

const unitTypes = [
  { value: 'appartement', label: 'Appartement' },
  { value: 'bureau', label: 'Bureau' },
  { value: 'entrepot', label: 'Entrepôt' },
  { value: 'local_commercial', label: 'Local commercial' },
  { value: 'parking', label: 'Parking' },
  { value: 'cave', label: 'Cave' },
  { value: 'autre', label: 'Autre' },
];

interface PropertyUnit {
  id: string;
  reference: string;
  type: string;
  areaSqm: string;
}

// For combined lots (e.g., bureau + entrepôt = 1 lot)
interface LotComponent {
  id: string;
  type: string;
  areaSqm: string;
}

export default function Properties() {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState<any>(null);
  const [editingProperty, setEditingProperty] = useState<any>(null);
  const [postalCodeError, setPostalCodeError] = useState<string>();
  const [selectedType, setSelectedType] = useState<string>('');
  const [units, setUnits] = useState<PropertyUnit[]>([]);
  // For combined lot mode
  const [lotReference, setLotReference] = useState('');
  const [lotComponents, setLotComponents] = useState<LotComponent[]>([]);
  const [createMode, setCreateMode] = useState<'single' | 'multi' | 'combined'>('single');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const queryClient = useQueryClient();

  const isMultiUnitType = selectedType === 'immeuble' || selectedType === 'zone_industrielle';

  const { data: properties = [], isLoading } = useQuery({
    queryKey: ['properties'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('*, scis(name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: scis = [] } = useQuery({
    queryKey: ['scis'],
    queryFn: async () => {
      const { data, error } = await supabase.from('scis').select('id, name');
      if (error) throw error;
      return data;
    },
  });

  const addUnit = () => {
    if (units.length >= 30) {
      toast.error('Maximum 30 unités par bien');
      return;
    }
    setUnits([...units, { 
      id: crypto.randomUUID(), 
      reference: '', 
      type: 'appartement',
      areaSqm: ''
    }]);
  };

  const updateUnit = (id: string, field: keyof PropertyUnit, value: string) => {
    setUnits(units.map(u => u.id === id ? { ...u, [field]: value } : u));
  };

  const removeUnit = (id: string) => {
    setUnits(units.filter(u => u.id !== id));
  };

  // Combined lot functions
  const addLotComponent = () => {
    if (lotComponents.length >= 10) {
      toast.error('Maximum 10 composants par lot');
      return;
    }
    setLotComponents([...lotComponents, { 
      id: crypto.randomUUID(), 
      type: 'bureau',
      areaSqm: ''
    }]);
  };

  const updateLotComponent = (id: string, field: keyof LotComponent, value: string) => {
    setLotComponents(lotComponents.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  const removeLotComponent = (id: string) => {
    setLotComponents(lotComponents.filter(c => c.id !== id));
  };

  const resetForm = () => {
    setSelectedType('');
    setUnits([]);
    setLotReference('');
    setLotComponents([]);
    setCreateMode('single');
    setPostalCodeError(undefined);
  };

  const createProperty = useMutation({
    mutationFn: async (formData: FormData) => {
      const postalCode = formData.get('postal_code') as string;
      if (postalCode) {
        const postalValidation = validatePostalCode(postalCode);
        if (!postalValidation.valid) {
          throw new Error(postalValidation.message);
        }
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const sciId = formData.get('sci_id') as string;
      const address = formData.get('address') as string;
      const city = formData.get('city') as string || null;
      const floor = formData.get('floor') as string || null;
      const acquisitionCost = formData.get('acquisition_cost') ? parseFloat(formData.get('acquisition_cost') as string) : null;
      const notes = formData.get('notes') as string || null;

      // Combined lot mode: creates ONE property with multiple components
      if (isMultiUnitType && createMode === 'combined' && lotComponents.length > 0) {
        const totalArea = lotComponents.reduce((sum, c) => sum + (parseFloat(c.areaSqm) || 0), 0);
        const typeLabels = lotComponents.map(c => {
          const typeLabel = unitTypes.find(t => t.value === c.type)?.label || c.type;
          return `${typeLabel} ${c.areaSqm ? c.areaSqm + 'm²' : ''}`;
        }).join(' + ');
        
        const combinedType = lotComponents.length === 1 
          ? lotComponents[0].type 
          : 'lot_mixte';
        
        const { error } = await supabase.from('properties').insert({
          organization_id: profile.organization_id,
          sci_id: sciId,
          name: lotReference || null,
          address: address,
          floor: floor,
          area_sqm: totalArea || null,
          postal_code: postalCode || null,
          city: city,
          type: combinedType,
          acquisition_cost: acquisitionCost,
          notes: notes ? `${typeLabels}\n${notes}` : typeLabels,
        });
        if (error) throw error;
      }
      // Multi-unit mode: creates MULTIPLE separate properties
      else if (isMultiUnitType && createMode === 'multi' && units.length > 0) {
        const propertiesToInsert = units.map(unit => ({
          organization_id: profile.organization_id,
          sci_id: sciId,
          name: unit.reference,
          address: address,
          floor: floor,
          area_sqm: unit.areaSqm ? parseFloat(unit.areaSqm) : null,
          postal_code: postalCode || null,
          city: city,
          type: unit.type,
          acquisition_cost: null,
          notes: notes,
        }));

        const { error } = await supabase.from('properties').insert(propertiesToInsert);
        if (error) throw error;
      } else {
        // Single property
        const { error } = await supabase.from('properties').insert({
          organization_id: profile.organization_id,
          sci_id: sciId,
          name: formData.get('name') as string || null,
          address: address,
          floor: floor,
          area_sqm: formData.get('area_sqm') ? parseFloat(formData.get('area_sqm') as string) : null,
          postal_code: postalCode || null,
          city: city,
          type: formData.get('type') as string || null,
          acquisition_cost: acquisitionCost,
          notes: notes,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setIsOpen(false);
      resetForm();
      const msg = createMode === 'combined' 
        ? 'Lot combiné créé avec succès' 
        : createMode === 'multi' && units.length > 0 
          ? `${units.length} biens créés avec succès` 
          : 'Bien créé avec succès';
      toast.success(msg);
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const updateProperty = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!editingProperty?.id) throw new Error('No property selected');

      const postalCode = formData.get('postal_code') as string;
      if (postalCode) {
        const postalValidation = validatePostalCode(postalCode);
        if (!postalValidation.valid) {
          throw new Error(postalValidation.message);
        }
      }

      const { error } = await supabase
        .from('properties')
        .update({
          sci_id: formData.get('sci_id') as string,
          name: formData.get('name') as string || null,
          address: formData.get('address') as string,
          floor: formData.get('floor') as string || null,
          area_sqm: formData.get('area_sqm') ? parseFloat(formData.get('area_sqm') as string) : null,
          postal_code: postalCode || null,
          city: formData.get('city') as string || null,
          type: formData.get('type') as string || null,
          acquisition_cost: formData.get('acquisition_cost') ? parseFloat(formData.get('acquisition_cost') as string) : null,
          notes: formData.get('notes') as string || null,
        })
        .eq('id', editingProperty.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      setEditingProperty(null);
      setIsEditOpen(false);
      setSelectedProperty(null);
      setPostalCodeError(undefined);
      toast.success('Bien mis à jour avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const deleteProperty = useMutation({
    mutationFn: async (propertyId: string) => {
      const { error } = await supabase
        .from('properties')
        .delete()
        .eq('id', propertyId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['properties'] });
      if (selectedProperty) setSelectedProperty(null);
      toast.success('Bien supprimé avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la suppression');
    },
  });

  const handlePostalCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value) {
      const validation = validatePostalCode(value);
      setPostalCodeError(validation.valid ? undefined : validation.message);
    } else {
      setPostalCodeError(undefined);
    }
  };

  const formatCurrency = (value: number | null) => {
    if (!value) return '-';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(value);
  };

  const getTypeLabel = (value: string) => {
    const allTypes = [...propertyTypes, ...unitTypes];
    return allTypes.find(t => t.value === value)?.label || value || '-';
  };

  const columns = [
    { header: 'Référence', accessorKey: 'name' as const },
    { header: 'SCI', accessorKey: (row: any) => row.scis?.name || '-' },
    { header: 'Type', accessorKey: (row: any) => getTypeLabel(row.type) },
    {
      header: 'Adresse',
      accessorKey: (row: any) => (
        <div className="flex flex-col gap-1">
          <span className="flex items-center gap-1"><MapPin className="h-3 w-3" /> {row.address}</span>
          {(row.postal_code || row.city) && (
            <span className="text-sm text-muted-foreground ml-4">{row.postal_code} {row.city}</span>
          )}
        </div>
      ),
    },
    {
      header: 'Surface',
      accessorKey: (row: any) => row.area_sqm ? `${row.area_sqm} m²` : '-',
    },
    {
      header: 'Actions',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => setSelectedProperty(row)} className="gap-2">
            <Eye className="h-4 w-4" />
            Détails
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => {
                setEditingProperty(row);
                setIsEditOpen(true);
              }}>
                <Edit className="mr-2 h-4 w-4" />
                Modifier
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <DropdownMenuItem onSelect={(e) => e.preventDefault()} className="text-destructive">
                    <Trash2 className="mr-2 h-4 w-4" />
                    Supprimer
                  </DropdownMenuItem>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer le bien</AlertDialogTitle>
                    <AlertDialogDescription>
                      Êtes-vous sûr de vouloir supprimer le bien "{row.name || row.address}" ? Cette action est irréversible et supprimera également tous les contrats associés.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteProperty.mutate(row.id)}
                      disabled={deleteProperty.isPending}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {deleteProperty.isPending ? 'Suppression...' : 'Supprimer'}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ];

  const filteredProperties = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    if (!term) return properties;
    return properties.filter((p: any) => {
      const name = (p.name || '').toLowerCase();
      const address = (p.address || '').toLowerCase();
      const city = (p.city || '').toLowerCase();
      const postalCode = (p.postal_code || '').toLowerCase();
      const sciName = (p.scis?.name || '').toLowerCase();
      const type = (p.type || '').toLowerCase();
      return name.includes(term) || address.includes(term) || city.includes(term) || postalCode.includes(term) || sciName.includes(term) || type.includes(term);
    });
  }, [properties, searchTerm]);

  return (
    <AppLayout title="Biens" description="Gérez vos biens immobiliers">
      <PageHeader
        title="Biens"
        description={`${properties.length} bien${properties.length > 1 ? 's' : ''}`}
        actions={
          <div className="flex gap-2">
            <ImportPropertiesDialog />
            <Dialog open={isOpen} onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> Nouveau bien</Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle>Nouveau bien</DialogTitle>
                </DialogHeader>
                <div className="flex-1 overflow-y-auto pr-4">
                  <form
                    id="property-form"
                    onSubmit={(e) => {
                      e.preventDefault();
                      createProperty.mutate(new FormData(e.currentTarget));
                    }}
                    className="space-y-4 pb-4"
                  >
                    <div className="space-y-2">
                      <Label>SCI *</Label>
                      <Select name="sci_id" required>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner une SCI" />
                        </SelectTrigger>
                        <SelectContent position="popper" className="z-[200]">
                          {scis.map((sci) => (
                            <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label>Type de bien *</Label>
                      <Select name="type" value={selectedType} onValueChange={setSelectedType} required>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionner le type" />
                        </SelectTrigger>
                        <SelectContent position="popper" className="z-[200]">
                          {propertyTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="address">Adresse *</Label>
                      <Input id="address" name="address" required placeholder="12 rue de la Paix" />
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="floor">Étage / Bâtiment</Label>
                        <Input id="floor" name="floor" placeholder="Bât. A" />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="postal_code">Code postal</Label>
                        <Input 
                          id="postal_code" 
                          name="postal_code" 
                          maxLength={5} 
                          placeholder="75001"
                          onChange={handlePostalCodeChange}
                        />
                        {postalCodeError && <p className="text-sm text-destructive">{postalCodeError}</p>}
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="city">Ville</Label>
                        <Input id="city" name="city" placeholder="Paris" />
                      </div>
                    </div>

                    {/* Multi-unit section for buildings and industrial zones */}
                    {isMultiUnitType && (
                      <>
                        <Separator className="my-4" />
                        <div className="space-y-4">
                          {/* Mode selector */}
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={createMode === 'single' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCreateMode('single')}
                              className="flex-1"
                            >
                              Bien unique
                            </Button>
                            <Button
                              type="button"
                              variant={createMode === 'multi' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCreateMode('multi')}
                              className="flex-1"
                            >
                              Plusieurs lots séparés
                            </Button>
                            <Button
                              type="button"
                              variant={createMode === 'combined' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setCreateMode('combined')}
                              className="flex-1"
                            >
                              Lot combiné
                            </Button>
                          </div>

                          {/* Combined lot mode */}
                          {createMode === 'combined' && (
                            <div className="space-y-4">
                              <div className="space-y-2">
                                <Label htmlFor="lot_reference">Référence du lot</Label>
                                <Input
                                  id="lot_reference"
                                  value={lotReference}
                                  onChange={(e) => setLotReference(e.target.value)}
                                  placeholder="Lot A, Bureau 1..."
                                />
                              </div>

                              <div className="flex items-center justify-between">
                                <div>
                                  <Label className="text-base flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    Composants du lot
                                  </Label>
                                  <p className="text-sm text-muted-foreground">
                                    Ex: Bureau 50m² + Entrepôt 100m² = 1 seul lot
                                  </p>
                                </div>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={addLotComponent}
                                  disabled={lotComponents.length >= 10}
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Ajouter
                                </Button>
                              </div>

                              {lotComponents.length === 0 && (
                                <div className="bg-muted/50 rounded-lg p-4 text-center text-muted-foreground">
                                  <p>Ajoutez les composants de votre lot (bureau, entrepôt, etc.)</p>
                                </div>
                              )}

                              {lotComponents.map((comp, index) => (
                                <Card key={comp.id} className="relative">
                                  <CardContent className="pt-4">
                                    <div className="absolute top-2 right-2">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeLotComponent(comp.id)}
                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-2 gap-3">
                                      <div className="space-y-1">
                                        <Label className="text-xs">Type *</Label>
                                        <Select
                                          value={comp.type}
                                          onValueChange={(v) => updateLotComponent(comp.id, 'type', v)}
                                        >
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent position="popper" className="z-[200]">
                                            {unitTypes.map((type) => (
                                              <SelectItem key={type.value} value={type.value}>
                                                {type.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Surface (m²) *</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={comp.areaSqm}
                                          onChange={(e) => updateLotComponent(comp.id, 'areaSqm', e.target.value)}
                                          placeholder="50"
                                          required
                                        />
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}

                              {lotComponents.length > 0 && (
                                <div className="bg-primary/10 rounded-lg p-3 text-sm">
                                  <p className="font-medium text-primary">
                                    1 lot combiné : {lotComponents.map(c => {
                                      const label = unitTypes.find(t => t.value === c.type)?.label || c.type;
                                      return `${label} ${c.areaSqm || '?'}m²`;
                                    }).join(' + ')}
                                  </p>
                                  <p className="text-muted-foreground mt-1">
                                    Surface totale: {lotComponents.reduce((sum, c) => sum + (parseFloat(c.areaSqm) || 0), 0)} m²
                                  </p>
                                </div>
                              )}
                            </div>
                          )}

                          {/* Multiple separate lots mode */}
                          {createMode === 'multi' && (
                            <div className="space-y-4">
                              <div className="flex items-center justify-between">
                                <div>
                                  <Label className="text-base flex items-center gap-2">
                                    <Building2 className="h-4 w-4" />
                                    Lots séparés
                                  </Label>
                                  <p className="text-sm text-muted-foreground">
                                    Chaque lot sera créé comme un bien distinct
                                  </p>
                                </div>
                                <Button 
                                  type="button" 
                                  variant="outline" 
                                  size="sm"
                                  onClick={addUnit}
                                  disabled={units.length >= 30}
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Ajouter un lot
                                </Button>
                              </div>

                              {units.length === 0 && (
                                <div className="bg-muted/50 rounded-lg p-4 text-center text-muted-foreground">
                                  <p>Aucun lot ajouté. Cliquez sur "Ajouter un lot".</p>
                                </div>
                              )}

                              {units.map((unit, index) => (
                                <Card key={unit.id} className="relative">
                                  <CardContent className="pt-4">
                                    <div className="absolute top-2 right-2">
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        onClick={() => removeUnit(unit.id)}
                                        className="h-8 w-8 text-destructive hover:text-destructive"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3">
                                      <div className="space-y-1">
                                        <Label className="text-xs">Référence *</Label>
                                        <Input
                                          value={unit.reference}
                                          onChange={(e) => updateUnit(unit.id, 'reference', e.target.value)}
                                          placeholder={`Lot ${index + 1}`}
                                          required
                                        />
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Type *</Label>
                                        <Select
                                          value={unit.type}
                                          onValueChange={(v) => updateUnit(unit.id, 'type', v)}
                                        >
                                          <SelectTrigger>
                                            <SelectValue />
                                          </SelectTrigger>
                                          <SelectContent position="popper" className="z-[200]">
                                            {unitTypes.map((type) => (
                                              <SelectItem key={type.value} value={type.value}>
                                                {type.label}
                                              </SelectItem>
                                            ))}
                                          </SelectContent>
                                        </Select>
                                      </div>
                                      <div className="space-y-1">
                                        <Label className="text-xs">Surface (m²)</Label>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={unit.areaSqm}
                                          onChange={(e) => updateUnit(unit.id, 'areaSqm', e.target.value)}
                                          placeholder="50"
                                        />
                                      </div>
                                    </div>
                                  </CardContent>
                                </Card>
                              ))}

                              {units.length > 0 && (
                                <div className="bg-primary/10 rounded-lg p-3 text-sm">
                                  <p className="font-medium text-primary">
                                    {units.length} lot{units.length > 1 ? 's' : ''} sera{units.length > 1 ? 'ont' : ''} créé{units.length > 1 ? 's' : ''}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </>
                    )}

                    {/* Single property fields (shown for single mode or non-multi-unit types) */}
                    {(!isMultiUnitType || createMode === 'single') && (
                      <>
                        <Separator className="my-4" />
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="name">Référence du lot</Label>
                            <Input id="name" name="name" placeholder="Apt 101, Bureau A..." />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="area_sqm">Surface (m²)</Label>
                            <Input id="area_sqm" name="area_sqm" type="number" step="0.01" placeholder="45" />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="acquisition_cost">Coût d'acquisition (€)</Label>
                          <Input id="acquisition_cost" name="acquisition_cost" type="number" step="0.01" placeholder="150000" />
                        </div>
                      </>
                    )}

                    {/* Acquisition cost for combined mode */}
                    {isMultiUnitType && createMode === 'combined' && (
                      <div className="space-y-2">
                        <Label htmlFor="acquisition_cost">Coût d'acquisition (€)</Label>
                        <Input id="acquisition_cost" name="acquisition_cost" type="number" step="0.01" placeholder="150000" />
                      </div>
                    )}

                    <div className="space-y-2">
                      <Label htmlFor="notes">Notes</Label>
                      <Input id="notes" name="notes" placeholder="Informations complémentaires" />
                    </div>

                    <Button type="submit" className="w-full" disabled={createProperty.isPending || !!postalCodeError}>
                      {createProperty.isPending 
                        ? 'Création...' 
                        : createMode === 'combined' && lotComponents.length > 0
                          ? 'Créer le lot combiné'
                          : createMode === 'multi' && units.length > 0 
                            ? `Créer ${units.length} bien${units.length > 1 ? 's' : ''}`
                            : 'Créer le bien'
                      }
                    </Button>
                  </form>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        }
      />
      
      {/* Search */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un bien..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredProperties}
        isLoading={isLoading}
        emptyMessage="Aucun bien. Créez d'abord une SCI, puis ajoutez des biens."
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background border rounded-lg shadow-lg px-4 py-3 flex items-center gap-4">
          <span className="text-sm font-medium">{selectedIds.size} sélectionné(s)</span>
          <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !bulkDeleteLoading && setBulkDeleteOpen(open)}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm"><Trash2 className="h-4 w-4 mr-2" /> Supprimer</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer {selectedIds.size} bien(s) ?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>Cette action est <strong>irréversible</strong>.</p>
                    <p className="text-destructive font-medium">⚠️ Toutes les données liées seront supprimées :</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Contrats de location associés</li>
                      <li>Périodes de loyer, paiements et allocations</li>
                      <li>Quittances et appels de fonds</li>
                      <li>Crédits immobiliers et documents</li>
                    </ul>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={bulkDeleteLoading}>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={bulkDeleteLoading}
                  onClick={async () => {
                    setBulkDeleteLoading(true);
                    let successCount = 0;
                    const totalCount = selectedIds.size;
                    try {
                      for (const id of selectedIds) {
                        try {
                          const { error } = await supabase.from('properties').delete().eq('id', id);
                          if (error) throw error;
                          successCount++;
                        } catch (err: any) {
                          console.error(`Échec suppression bien ${id}:`, err);
                          toast.error(`Erreur sur un bien : ${err.message || 'Erreur inconnue'}`);
                        }
                      }
                      queryClient.invalidateQueries({ queryKey: ['properties'] });
                      queryClient.invalidateQueries({ queryKey: ['leases'] });
                      queryClient.invalidateQueries({ queryKey: ['receipts'] });
                      queryClient.invalidateQueries({ queryKey: ['call_for_funds'] });
                      queryClient.invalidateQueries({ queryKey: ['payments'] });
                      queryClient.invalidateQueries({ queryKey: ['rent_periods'] });
                      if (successCount === totalCount) {
                        toast.success(`${successCount} bien(s) supprimé(s)`);
                      } else if (successCount > 0) {
                        toast.warning(`${successCount}/${totalCount} bien(s) supprimé(s). ${totalCount - successCount} erreur(s).`);
                      }
                      setSelectedIds(new Set());
                    } finally {
                      setBulkDeleteLoading(false);
                      setBulkDeleteOpen(false);
                    }
                  }}
                >
                  {bulkDeleteLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Suppression...</> : 'Supprimer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} disabled={bulkDeleteLoading}>Annuler</Button>
        </div>
      )}

      <Sheet open={!!selectedProperty} onOpenChange={() => setSelectedProperty(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedProperty && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  {selectedProperty.name || selectedProperty.address}
                </SheetTitle>
              </SheetHeader>

              <Tabs defaultValue="info" className="mt-6">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="info">Infos</TabsTrigger>
                  <TabsTrigger value="credits">Crédits</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Informations générales</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Type</p>
                          <p className="font-medium">{getTypeLabel(selectedProperty.type)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">SCI</p>
                          <p className="font-medium">{selectedProperty.scis?.name || '-'}</p>
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Adresse</p>
                        <p className="font-medium flex items-center gap-1">
                          <MapPin className="h-4 w-4" />
                          {selectedProperty.address}
                          {selectedProperty.floor && `, ${selectedProperty.floor}`}
                        </p>
                        <p className="text-sm text-muted-foreground ml-5">
                          {selectedProperty.postal_code} {selectedProperty.city}
                        </p>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="flex items-center gap-2">
                          <Ruler className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Surface</p>
                            <p className="font-medium">{selectedProperty.area_sqm ? `${selectedProperty.area_sqm} m²` : '-'}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Euro className="h-4 w-4 text-muted-foreground" />
                          <div>
                            <p className="text-sm text-muted-foreground">Coût d'acquisition</p>
                            <p className="font-medium">{formatCurrency(selectedProperty.acquisition_cost)}</p>
                          </div>
                        </div>
                      </div>
                      {selectedProperty.notes && (
                        <div>
                          <p className="text-sm text-muted-foreground">Notes</p>
                          <p>{selectedProperty.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  <Button
                    onClick={() => {
                      setEditingProperty(selectedProperty);
                      setIsEditOpen(true);
                    }}
                    className="w-full"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Modifier
                  </Button>
                </TabsContent>

                <TabsContent value="credits" className="mt-4">
                  <PropertyMortgages propertyId={selectedProperty.id} />
                </TabsContent>

                <TabsContent value="documents" className="mt-4">
                  <PropertyDocuments propertyId={selectedProperty.id} />
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => {
        setIsEditOpen(open);
        if (!open) {
          setEditingProperty(null);
          setPostalCodeError(undefined);
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le bien</DialogTitle>
          </DialogHeader>
          {editingProperty && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateProperty.mutate(new FormData(e.currentTarget));
              }}
              className="space-y-4"
            >
              <div className="space-y-2">
                <Label>SCI *</Label>
                <Select name="sci_id" required defaultValue={editingProperty.sci_id}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une SCI" />
                  </SelectTrigger>
                  <SelectContent>
                    {scis.map((sci) => (
                      <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Type de bien *</Label>
                <Select name="type" required defaultValue={editingProperty.type || ''}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner le type" />
                  </SelectTrigger>
                  <SelectContent>
                    {propertyTypes.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-address">Adresse *</Label>
                <Input
                  id="edit-address"
                  name="address"
                  required
                  defaultValue={editingProperty.address}
                  placeholder="12 rue de la Paix"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-floor">Étage / Bâtiment</Label>
                  <Input
                    id="edit-floor"
                    name="floor"
                    defaultValue={editingProperty.floor || ''}
                    placeholder="Bât. A"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-postal_code">Code postal</Label>
                  <Input
                    id="edit-postal_code"
                    name="postal_code"
                    maxLength={5}
                    placeholder="75001"
                    defaultValue={editingProperty.postal_code || ''}
                    onChange={handlePostalCodeChange}
                  />
                  {postalCodeError && <p className="text-sm text-destructive">{postalCodeError}</p>}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-city">Ville</Label>
                  <Input
                    id="edit-city"
                    name="city"
                    placeholder="Paris"
                    defaultValue={editingProperty.city || ''}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Référence du lot</Label>
                  <Input
                    id="edit-name"
                    name="name"
                    defaultValue={editingProperty.name || ''}
                    placeholder="Apt 101, Bureau A..."
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-area_sqm">Surface (m²)</Label>
                  <Input
                    id="edit-area_sqm"
                    name="area_sqm"
                    type="number"
                    step="0.01"
                    placeholder="45"
                    defaultValue={editingProperty.area_sqm || ''}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-acquisition_cost">Coût d'acquisition (€)</Label>
                <Input
                  id="edit-acquisition_cost"
                  name="acquisition_cost"
                  type="number"
                  step="0.01"
                  placeholder="150000"
                  defaultValue={editingProperty.acquisition_cost || ''}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes</Label>
                <Input
                  id="edit-notes"
                  name="notes"
                  defaultValue={editingProperty.notes || ''}
                  placeholder="Informations complémentaires"
                />
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setIsEditOpen(false);
                    setEditingProperty(null);
                    setPostalCodeError(undefined);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={updateProperty.isPending || !!postalCodeError}
                >
                  {updateProperty.isPending ? 'Mise à jour...' : 'Mettre à jour'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
